import type { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'

import type { FinData } from 'partner360/entities/pbmDashboard'

import type { SobNodeOrderResolver } from '../model/types'

import {
  buildSobSettingsCategoryKey,
  buildSobSettingsKey,
  isOverallSettingsHierarchyValue,
} from './sobSettingsKeys'

export const buildSobAvailableSettingsByKey = ({
  settings,
  offering,
  category,
}: {
  settings: IFormActualsTargetsSettings[]
  offering: string
  category: string | null
}) => {
  const settingsByKey = new Map<string, IFormActualsTargetsSettings>()

  settings.forEach((setting) => {
    if (
      setting.ProgramType !== offering ||
      (category && setting.Category !== category) ||
      !setting.IsSoBAvailable
    ) {
      return
    }

    settingsByKey.set(
      buildSobSettingsKey({
        offering: setting.ProgramType,
        category: setting.Category,
        subCategory: setting.SubCategory,
        l3Category: setting.L3Category,
      }),
      setting
    )
  })

  return settingsByKey
}

export const buildSobSettingsByCategoryKey = ({
  settings,
  offering,
  category,
}: {
  settings: IFormActualsTargetsSettings[]
  offering: string
  category: string | null
}) => {
  const settingsByCategoryKey = new Map<string, IFormActualsTargetsSettings[]>()

  settings.forEach((setting) => {
    if (setting.ProgramType !== offering || (category && setting.Category !== category)) {
      return
    }

    const settingsCategoryKey = buildSobSettingsCategoryKey({
      offering: setting.ProgramType,
      category: setting.Category,
    })
    const categorySettings = settingsByCategoryKey.get(settingsCategoryKey) ?? []

    categorySettings.push(setting)
    settingsByCategoryKey.set(settingsCategoryKey, categorySettings)
  })

  return settingsByCategoryKey
}

const toOrderValue = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

/**
 * Resolves the display order of a hierarchy node from SOB settings so that the modal lists
 * categories, subcategories and L3 categories in the same order as the planning tab.
 */
export const createSobNodeOrderResolver =
  ({
    settingsByKey,
    offering,
  }: {
    settingsByKey: Map<string, IFormActualsTargetsSettings>
    offering: string
  }): SobNodeOrderResolver =>
  ({ category, subCategory, l3Category }) => {
    const setting = settingsByKey.get(
      buildSobSettingsKey({ offering, category, subCategory, l3Category })
    )

    if (!setting) {
      return undefined
    }

    if (!isOverallSettingsHierarchyValue(l3Category)) {
      return toOrderValue(setting.L3CategoryOrder)
    }

    if (!isOverallSettingsHierarchyValue(subCategory)) {
      return toOrderValue(setting.SubCategoryOrder)
    }

    return toOrderValue(setting.CategoryOrder)
  }

export const filterSobAvailableFinDataRows = ({
  finData,
  pid,
  offering,
  category,
  sobAvailableSettingsByKey,
}: {
  finData: FinData[]
  pid: string
  offering: string
  category: string | null
  sobAvailableSettingsByKey: Map<string, IFormActualsTargetsSettings>
}) =>
  finData
    .filter(
      (row) =>
        row.PartnerId === pid &&
        row.Offering === offering &&
        (!category || row.Category === category)
    )
    .filter((row) =>
      sobAvailableSettingsByKey.has(
        buildSobSettingsKey({
          offering: row.Offering,
          category: row.Category,
          subCategory: row.SubCategory,
          l3Category: row.L3Category,
        })
      )
    )
